'use client';

import type { BlocksContent } from '@strapi/blocks-react-renderer';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import { Button } from '@/components/Button';
import { locationLabel, type ZipCityLocation } from '@/components/ZipCity';
import {
  getPublicLocationBySlug,
  submitAdjustableContactForm,
  type LocationDto,
} from '@/lib/api';
import { generateFormCustomerId } from '@/lib/form/customerId';
import { useAppForm } from '@/lib/form/hook';
import { sourceUrlWithCampaign } from '@/lib/form/sourceUrl';
import type { ConfirmationEntity } from '@/lib/render/RenderContext';
import {
  useRenderContext,
  useSubstitute,
} from '@/lib/render/RenderContextProvider';
import { RichText } from '@/lib/render/RichText';
import { sanitize, sanitizeLinksOnly } from '@/lib/sanitize';

// --- Types -------------------------------------------------------------

/** Mirrors `shared.adjustable-contact-form-field`'s `type` enum (Task 5/6). */
export type AdjustableContactFormFieldType =
  | 'firstname'
  | 'lastname'
  | 'email'
  | 'phone'
  | 'free-text'
  | 'salutation-2-choices'
  | 'salutation-3-choices'
  | 'location'
  | 'plz-only'
  | 'checkbox-existing-customer'
  | 'checkbox-privacy';

/** One resolved `shared.adjustable-contact-form-field` instance. */
export interface AdjustableContactFormFieldResolved {
  type: AdjustableContactFormFieldType;
  /**
   * Optional in Strapi — an empty/absent label renders no visible heading
   * (the seeded salutation fields rely on this) and no sr-only label text.
   */
  label?: string;
  placeholder?: string;
  isRequired: boolean;
  width: '50' | '100';
  validationErrorText?: string;
}

/**
 * The subset of `widgets.adjustable-contact-form`'s resolved shape this
 * component needs. Task 11's widget-level `AdjustableContactFormResolved`
 * (confirmationBody, submitTracking, tracking, …) is a structural superset
 * of this — passing that object here is fine, this component just ignores
 * the extra keys.
 */
export interface AdjustableContactFormResolved {
  /**
   * Identifiers of the placed component and its owner, stamped on by the
   * backend (AM-SH-388). Forwarded on submit so the backend can read the
   * editor's delivery target — `sh-api` or a mailbox — from the database.
   * **Never a recipient address**: `/api/forms/dynamic` is public, so an
   * address accepted from the body would make it an open mail relay.
   *
   * `id` is a component *row* id and Strapi replaces it whenever its owner is
   * saved; the owner's documentId survives, which is why both travel. There is
   * exactly one owner: `pageDocumentId`/`pageLocale` for a form in the block
   * pipeline, `snippetDocumentId` for one inside a Content Snippet, and
   * `menuDocumentId`/`menuLocale` for the header contact modal, which mounts
   * outside the pipeline and has no Page.
   */
  id?: number;
  pageDocumentId?: string;
  pageLocale?: string;
  snippetDocumentId?: string;
  menuDocumentId?: string;
  menuLocale?: string;
  kind: 'contact' | 'voucher';
  fields: AdjustableContactFormFieldResolved[];
  submitButtonLabel?: string;
  submitButtonStyle?: 'primary' | 'secondary';
  /**
   * Strapi `richtext` field — resolves as a plain markdown string (not
   * `BlocksContent`; that's the `blocks` field type, used e.g. by
   * `contact-widget`'s `confirmationBody`). Rendered the same way
   * `icon-card-widget`/`subjects-widget` render their own `richtext`
   * fields: `sanitize(sub(text))` into `dangerouslySetInnerHTML`.
   */
  errorBannerText?: string;
  /**
   * Strapi `blocks` field ("Rich text (Blocks)") — general fill-out
   * instructions (e.g. "* - Pflichtfelder"), rendered full-width between
   * the last field and the submit button via `<RichText />` (which also
   * substitutes `{…}` placeholders in its text nodes).
   */
  formHint?: BlocksContent;
}

/**
 * Compile-time proof that the five identifier keys spread into the submit
 * payload below are the names `/api/forms/dynamic` actually accepts.
 *
 * They are added by conditional spread, so that the body of a form with no
 * identifiers stays exactly what it was before AM-SH-388 — and TypeScript does
 * not excess-property-check a spread, so a renamed or mistyped key would be
 * accepted here and then silently dropped by the backend's whitelist, leaving
 * every submission on the SH API Gateway with nothing to show for it. `Pick`
 * does check, and fails to compile on a key the generated client does not
 * have.
 */
type _FormOwnerIdKeys = Pick<
  Parameters<typeof submitAdjustableContactForm>[0],
  | 'adjustableContactFormId'
  | 'pageDocumentId'
  | 'pageLocale'
  | 'snippetDocumentId'
  | 'menuDocumentId'
  | 'menuLocale'
>;

export interface AdjustableContactFormProps {
  resolved: AdjustableContactFormResolved;
  className?: string;
  /**
   * Called after a successful submission (e.g. to show a confirmation view),
   * with the visitor-entered values as a `ConfirmationEntity` so callers can
   * expose them for `{confirmation.*}` placeholder substitution — built
   * purely from the client-side form state, never from the backend response.
   *
   * `confirmationLocation` is the full Standort the visitor picked in the
   * `location` field's ZipCity autocomplete, fetched from the public
   * `GET /api/locations/{slug}` endpoint — `undefined` when nothing was
   * selected (free text only) or the fetch failed.
   */
  onSuccess?: (
    confirmation: ConfirmationEntity,
    confirmationLocation?: LocationDto,
  ) => void;
  /**
   * Called right before the submit request is sent — after validation has
   * already passed, so this fires once per actual submission attempt that's
   * about to go out over the wire, not on every click regardless of
   * validity (e.g. to fire a pre-send GA4 event, mirroring
   * `contact-widget`'s `useContactSubmit.ts` calling
   * `pushCmsEventTracking(submitTracking)` right before its own
   * `submitForm(...)` call).
   */
  onSubmitAttempt?: () => void;
}

// --- Layout --------------------------------------------------------------

// Below this container width (not viewport width — this must respond to the
// form's own rendered width, e.g. embedded in a narrow sidebar on desktop),
// every field collapses to 100% regardless of its configured width.
const NARROW_CONTAINER_PX = 500;

function useNarrowContainer(ref: React.RefObject<HTMLElement | null>) {
  const [isNarrow, setIsNarrow] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (typeof width === 'number') {
        setIsNarrow(width < NARROW_CONTAINER_PX);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);

  return isNarrow;
}

// --- Validation ------------------------------------------------------------

const noAt = (v: string) => !v.includes('@');
const PHONE_REGEX = /^\+?\d{7,15}$/;
// 5 digits (DE), 4 (AT/CH) — mirrors SubmitFormBaseDTO.plz's `^\d{4,5}$`.
const PLZ_REGEX = /^\d{4,5}$/;
const isValidPhone = (v: string) =>
  noAt(v) && PHONE_REGEX.test(v.replace(/[\s\-/()]/g, ''));

const CHECKBOX_TYPES = new Set<AdjustableContactFormFieldType>([
  'checkbox-existing-customer',
  'checkbox-privacy',
]);

function isCheckboxField(type: AdjustableContactFormFieldType): boolean {
  return CHECKBOX_TYPES.has(type);
}

/**
 * One `field` plus a stable, collision-free identifier for it. `field.type`
 * alone can't serve as a React key / form-state key / Zod shape key /
 * `data-testid`, because an editor can configure two fields of the same
 * `type` (e.g. two `free-text` fields) — bare `field.type` would then
 * collide across all of those. Suffixing with the array index keeps each
 * field's identifier unique regardless of how many fields share a `type`.
 */
type KeyedField = AdjustableContactFormFieldResolved & { key: string };

// Field types whose value is mandatory on every backend DTO — enforced as
// required regardless of the editor's `isRequired` flag (the CMS rejects
// such configs on save too, see the Strapi Document Service middleware
// `validateRequiredFieldTypes.ts`; this is the belt to that suspender).
const ALWAYS_REQUIRED_TYPES = new Set<AdjustableContactFormFieldType>([
  'firstname',
  'lastname',
  'email',
]);

function keyFields(fields: AdjustableContactFormFieldResolved[]): KeyedField[] {
  return fields.map((field, index) => ({
    ...field,
    isRequired: field.isRequired || ALWAYS_REQUIRED_TYPES.has(field.type),
    key: `${field.type}-${index}`,
  }));
}

/** The field's own `validationErrorText`, falling back to a generic default. */
function fieldMessage(
  field: AdjustableContactFormFieldResolved,
  fallback: string,
): string {
  const trimmed = field.validationErrorText?.trim();
  return trimmed ? trimmed : fallback;
}

// Builds the Zod validator for one configured field, per Task 10's
// type → validator lookup table. Every failure mode of a given field shares
// the same message (the CMS only offers a single `validationErrorText` per
// field, not one per rule) — a deliberate simplification, see task report.
function buildFieldSchema(
  field: AdjustableContactFormFieldResolved,
  t: (key: string) => string,
): z.ZodType {
  switch (field.type) {
    case 'firstname':
    case 'lastname': {
      const msg = fieldMessage(
        field,
        t(
          field.type === 'firstname' ? 'firstnameRequired' : 'lastnameRequired',
        ),
      );
      return field.isRequired
        ? z.string().trim().min(1, msg).refine(noAt, msg)
        : z
            .string()
            .trim()
            .refine((v) => v === '' || noAt(v), msg);
    }
    case 'email': {
      const msg = fieldMessage(field, t('emailRequired'));
      return field.isRequired
        ? z.string().trim().min(1, msg).pipe(z.email(msg))
        : z
            .string()
            .trim()
            .refine((v) => v === '' || z.email().safeParse(v).success, msg);
    }
    case 'phone': {
      const msg = fieldMessage(field, t('phoneRequired'));
      return field.isRequired
        ? z.string().trim().min(1, msg).refine(isValidPhone, msg)
        : z
            .string()
            .trim()
            .refine((v) => v === '' || isValidPhone(v), msg);
    }
    case 'free-text': {
      const msg = fieldMessage(field, t('messageRequired'));
      return field.isRequired
        ? z.string().trim().min(1, msg).refine(noAt, msg)
        : z
            .string()
            .trim()
            .refine((v) => v === '' || noAt(v), msg);
    }
    case 'salutation-2-choices': {
      const msg = fieldMessage(field, t('salutationRequired'));
      return field.isRequired
        ? z.enum(['mr', 'mrs'], { message: msg })
        : z.string();
    }
    case 'salutation-3-choices': {
      const msg = fieldMessage(field, t('salutationRequired'));
      return field.isRequired
        ? z.enum(['mr', 'mrs', 'none'], { message: msg })
        : z.string();
    }
    case 'location': {
      const msg = fieldMessage(field, t('locationRequired'));
      return field.isRequired ? z.string().trim().min(1, msg) : z.string();
    }
    case 'plz-only': {
      const msg = fieldMessage(field, t('plzRequired'));
      return field.isRequired
        ? z.string().trim().regex(PLZ_REGEX, msg)
        : z
            .string()
            .trim()
            .refine((v) => v === '' || PLZ_REGEX.test(v), msg);
    }
    // Yes/no answer, not a consent gate — never required to be `true`.
    case 'checkbox-existing-customer':
      return z.boolean();
    case 'checkbox-privacy': {
      const msg = fieldMessage(field, t('consentRequired'));
      return field.isRequired ? z.literal(true, { message: msg }) : z.boolean();
    }
  }
}

// --- Component ---------------------------------------------------------

export function AdjustableContactForm({
  resolved,
  className,
  onSuccess,
  onSubmitAttempt,
}: AdjustableContactFormProps) {
  const tForm = useTranslations('form');
  const tValidation = useTranslations('validation');
  const { entities } = useRenderContext();
  const contextLocationSlug = entities.location?.slug;

  const [hasError, setHasError] = useState(false);
  const containerRef = useRef<HTMLFormElement>(null);
  const isNarrow = useNarrowContainer(containerRef);

  // Generated once per mount, exactly like <ContactForm /> (AM-SH-378): the
  // mandatory `customerNumber` submitted to the backend, also mirrored into
  // the hidden `.form-customerId` DOM field that IntelliAd reads.
  const [customerId] = useState(() =>
    generateFormCustomerId(entities.location?.centerNumber),
  );

  // The Standort picked in the `location` field's ZipCity autocomplete.
  // Cleared again when the visitor edits the input text away from the
  // selection's label — then the raw text is submitted as before.
  const [selectedLocation, setSelectedLocation] =
    useState<ZipCityLocation | null>(null);
  // Full-DTO fetch kicked off eagerly on selection (keyed by slug so a
  // stale response for a previously selected Standort is never used);
  // awaited only after a successful submission, for `confirmationLocation`.
  const locationFetchRef = useRef<{
    slug: string;
    promise: Promise<LocationDto | null>;
  } | null>(null);

  const handleLocationSelect = (loc: ZipCityLocation) => {
    setSelectedLocation(loc);
    locationFetchRef.current = {
      slug: loc.slug,
      promise: getPublicLocationBySlug(loc.slug),
    };
  };

  const handleLocationTextChange = (value: string) => {
    setSelectedLocation((prev) =>
      prev && value !== locationLabel(prev) ? null : prev,
    );
  };

  const { fields } = resolved;

  // Index-suffixed keys (see `KeyedField`) so two fields sharing a `type`
  // (e.g. two `free-text` fields) don't collide as form-state/Zod-shape keys.
  const keyedFields = useMemo(() => keyFields(fields), [fields]);

  const defaultValues = useMemo(() => {
    const values: Record<string, string | boolean> = {};
    for (const field of keyedFields) {
      values[field.key] = isCheckboxField(field.type) ? false : '';
    }
    return values;
  }, [keyedFields]);

  const shape = useMemo(() => {
    const result: Record<string, z.ZodType> = {};
    for (const field of keyedFields) {
      result[field.key] = buildFieldSchema(field, tValidation);
    }
    return result;
  }, [keyedFields, tValidation]);

  const form = useAppForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      setHasError(false);
      // Field-level validators have already passed by the time TanStack Form
      // calls onSubmit, so this combined parse is expected to succeed — but a
      // failure must surface as the error banner, not as an exception
      // escaping onSubmit (only the submit call below is try/caught).
      const result = z.object(shape).safeParse(value);
      if (!result.success) {
        setHasError(true);
        return;
      }
      const parsed = result.data as Record<string, string | boolean>;

      // Each configured `type` is assumed to appear at most once for the
      // purposes of building the fixed submit payload below (the CMS models
      // e.g. `firstname`/`email` as singular concepts) — `keyedFields` keys
      // only exist to avoid key *collisions*, not to support multiple
      // semantic values per type.
      const valueForType = (type: AdjustableContactFormFieldType): string => {
        const field = keyedFields.find((f) => f.type === type);
        return field ? ((parsed[field.key] as string) ?? '') : '';
      };

      const salutationField = keyedFields.find(
        (f) =>
          f.type === 'salutation-2-choices' ||
          f.type === 'salutation-3-choices',
      );
      const salutation = salutationField
        ? (parsed[salutationField.key] as string) || 'none'
        : 'none';

      const locationField = keyedFields.find((f) => f.type === 'location');
      const fieldLocationText = locationField
        ? (parsed[locationField.key] as string)
        : '';
      // A ZipCity selection wins (the visible input then holds a display
      // label like "40210 - Düsseldorf", not a slug); raw typed text keeps
      // the previous behavior, falling back to the context location.
      const locationSlug =
        selectedLocation?.slug ||
        fieldLocationText ||
        contextLocationSlug ||
        '';

      const payload: Parameters<typeof submitAdjustableContactForm>[0] = {
        kind: resolved.kind,
        // Names the component, not the recipient — the backend reads the
        // address from this form's own CMS record (AM-SH-388). The owner's
        // documentId is the stable half of the pair: `id` is a component row
        // id and Strapi replaces it whenever the owner is saved. Spread
        // conditionally so a form built without them (the CMS preview, a
        // hand-made test config) posts exactly the body it posted before.
        ...(resolved.id != null
          ? { adjustableContactFormId: resolved.id }
          : {}),
        ...(resolved.pageDocumentId
          ? { pageDocumentId: resolved.pageDocumentId }
          : {}),
        ...(resolved.pageLocale ? { pageLocale: resolved.pageLocale } : {}),
        ...(resolved.snippetDocumentId
          ? { snippetDocumentId: resolved.snippetDocumentId }
          : {}),
        ...(resolved.menuDocumentId
          ? { menuDocumentId: resolved.menuDocumentId }
          : {}),
        ...(resolved.menuLocale ? { menuLocale: resolved.menuLocale } : {}),
        salutation: salutation as 'mr' | 'mrs' | 'none',
        firstname: valueForType('firstname'),
        lastname: valueForType('lastname'),
        email: valueForType('email'),
        phone: valueForType('phone'),
        locationSlug,
        sourceUrl: sourceUrlWithCampaign(),
        customerNumber: customerId,
      };

      const freeTextField = keyedFields.find((f) => f.type === 'free-text');
      if (freeTextField) {
        payload.message = parsed[freeTextField.key] as string;
      }

      // `plz-only`: a bare PLZ, submitted as `plz` and consumed by the
      // backend as-is — deliberately NOT a location lookup (no slug, no
      // resolution), unlike the `location` field.
      const plzOnlyValue = valueForType('plz-only');
      if (keyedFields.some((f) => f.type === 'plz-only')) {
        payload.plz = plzOnlyValue;
      }

      // Submitted under the CMS field-type name; the backend renames it to
      // `isAlreadyCustomer` and forwards it to the SH API Gateway (SH-558).
      // A form without the checkbox submits no key at all — absent and
      // `false` are different answers.
      const existingCustomerField = keyedFields.find(
        (f) => f.type === 'checkbox-existing-customer',
      );
      if (existingCustomerField) {
        payload['checkbox-existing-customer'] = parsed[
          existingCustomerField.key
        ] as boolean;
      }

      // `checkbox-privacy` is intentionally never added to `payload` — it's
      // a client-side-only consent gate.

      try {
        onSubmitAttempt?.();
        await submitAdjustableContactForm(payload);

        // Resolve the ZipCity-selected Standort for {confirmationLocation.*}
        // — the fetch has been running since selection, so this normally
        // resolves instantly; null (failed fetch) degrades to undefined.
        let confirmationLocation: LocationDto | undefined;
        const locationFetch = locationFetchRef.current;
        if (selectedLocation && locationFetch?.slug === selectedLocation.slug) {
          confirmationLocation = (await locationFetch.promise) ?? undefined;
        }

        onSuccess?.(
          {
            // The localized label the visitor picked, not the wire value —
            // '' for "none"/no salutation field, since "Keine Angabe" reads
            // wrong inside confirmation copy like "Sehr geehrte(r) {gender}".
            gender:
              salutation === 'mr' || salutation === 'mrs'
                ? tForm(salutation)
                : '',
            firstName: valueForType('firstname'),
            lastName: valueForType('lastname'),
            // The explicit `plz-only` value when the form has one,
            // otherwise the selected Standort's postcode, otherwise what
            // the visitor typed into the "PLZ oder Ort" field — never the
            // context-location fallback that `payload.locationSlug` may
            // carry.
            plz:
              plzOnlyValue || (selectedLocation?.postcode ?? fieldLocationText),
          },
          confirmationLocation,
        );
      } catch {
        setHasError(true);
      }
    },
  });

  const salutationOptions2 = [
    { value: 'mr', label: tForm('mr') },
    { value: 'mrs', label: tForm('mrs') },
  ];
  const salutationOptions3 = [
    ...salutationOptions2,
    { value: 'none', label: tForm('noSalutation') },
  ];

  const sub = useSubstitute();
  const errorBannerHtml = resolved.errorBannerText
    ? sanitize(sub(resolved.errorBannerText))
    : null;

  return (
    <form
      ref={containerRef}
      onSubmit={(e) => {
        e.preventDefault();
        form.handleSubmit();
      }}
      className={className}
      noValidate
    >
      <input
        type="hidden"
        className="form-customerId"
        value={customerId}
        readOnly
      />
      {hasError && (
        <div role="alert" className="mb-4 text-sm text-red-600">
          {errorBannerHtml ? (
            <div dangerouslySetInnerHTML={{ __html: errorBannerHtml }} />
          ) : (
            <p>{tForm('submissionError')}</p>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-4">
        {keyedFields.map((field) => {
          const schema = shape[field.key];
          const collapsed = isNarrow || field.width === '100';
          // Editor-typed, so it may carry placeholders — including
          // `{location.privacyPolicyPath}` inside the consent checkbox's
          // markdown privacy links, which is what points them at this
          // Standort's own Datenschutzerklärung.
          const label = sub(field.label ?? '');
          return (
            <div
              key={field.key}
              data-testid={`field-wrapper-${field.key}`}
              style={{ flexBasis: collapsed ? '100%' : 'calc(50% - 0.5rem)' }}
              className="min-w-0 shrink-0 grow-0"
            >
              {field.type === 'firstname' && (
                <form.AppField
                  name={field.key}
                  validators={{
                    onBlur: schema as never,
                    onSubmit: schema as never,
                  }}
                >
                  {(f) => (
                    <f.TextField
                      label={label}
                      autoComplete="given-name"
                      required={field.isRequired}
                      placeholder={field.placeholder}
                    />
                  )}
                </form.AppField>
              )}
              {field.type === 'lastname' && (
                <form.AppField
                  name={field.key}
                  validators={{
                    onBlur: schema as never,
                    onSubmit: schema as never,
                  }}
                >
                  {(f) => (
                    <f.TextField
                      label={label}
                      autoComplete="family-name"
                      required={field.isRequired}
                      placeholder={field.placeholder}
                    />
                  )}
                </form.AppField>
              )}
              {field.type === 'email' && (
                <form.AppField
                  name={field.key}
                  validators={{
                    onBlur: schema as never,
                    onSubmit: schema as never,
                  }}
                >
                  {(f) => (
                    <f.TextField
                      label={label}
                      type="email"
                      autoComplete="email"
                      required={field.isRequired}
                      placeholder={field.placeholder}
                    />
                  )}
                </form.AppField>
              )}
              {field.type === 'phone' && (
                <form.AppField
                  name={field.key}
                  validators={{
                    onBlur: schema as never,
                    onSubmit: schema as never,
                  }}
                >
                  {(f) => (
                    <f.TextField
                      label={label}
                      type="tel"
                      autoComplete="tel"
                      required={field.isRequired}
                      placeholder={field.placeholder}
                    />
                  )}
                </form.AppField>
              )}
              {field.type === 'free-text' && (
                <form.AppField
                  name={field.key}
                  validators={{
                    onBlur: schema as never,
                    onSubmit: schema as never,
                  }}
                >
                  {(f) => (
                    <f.TextareaField
                      label={label}
                      required={field.isRequired}
                      placeholder={field.placeholder}
                    />
                  )}
                </form.AppField>
              )}
              {field.type === 'location' && (
                // ZipCity autocomplete (the shared location search): the
                // visitor types a PLZ or city and picks a Standort — the
                // selection's slug is submitted as `locationSlug` (see
                // `handleLocationSelect`). Free text without a selection is
                // submitted as-is, like the previous plain text field.
                <form.AppField
                  name={field.key}
                  validators={{
                    onBlur: schema as never,
                    onSubmit: schema as never,
                  }}
                >
                  {(f) => (
                    <f.ZipCityField
                      required={field.isRequired}
                      placeholder={field.placeholder ?? label}
                      onSelect={handleLocationSelect}
                      onTextChange={handleLocationTextChange}
                    />
                  )}
                </form.AppField>
              )}
              {field.type === 'plz-only' && (
                // Bare PLZ input — no ZipCity search, the value is submitted
                // as `plz` without any location resolution.
                <form.AppField
                  name={field.key}
                  validators={{
                    onBlur: schema as never,
                    onSubmit: schema as never,
                  }}
                >
                  {(f) => (
                    <f.TextField
                      label={label}
                      autoComplete="postal-code"
                      required={field.isRequired}
                      placeholder={field.placeholder}
                    />
                  )}
                </form.AppField>
              )}
              {field.type === 'salutation-2-choices' && (
                <form.AppField
                  name={field.key}
                  validators={{
                    onChange: schema as never,
                    onSubmit: schema as never,
                  }}
                >
                  {(f) => (
                    <f.RadioGroup
                      options={salutationOptions2}
                      required={field.isRequired}
                      label={label}
                    />
                  )}
                </form.AppField>
              )}
              {field.type === 'salutation-3-choices' && (
                <form.AppField
                  name={field.key}
                  validators={{
                    onChange: schema as never,
                    onSubmit: schema as never,
                  }}
                >
                  {(f) => (
                    <f.RadioGroup
                      options={salutationOptions3}
                      required={field.isRequired}
                      label={label}
                    />
                  )}
                </form.AppField>
              )}
              {field.type === 'checkbox-existing-customer' && (
                <form.AppField
                  name={field.key}
                  validators={{
                    onChange: schema as never,
                    onSubmit: schema as never,
                  }}
                >
                  {(f) => <f.Checkbox>{label}</f.Checkbox>}
                </form.AppField>
              )}
              {field.type === 'checkbox-privacy' && (
                <form.AppField
                  name={field.key}
                  validators={{
                    onChange: schema as never,
                    onSubmit: schema as never,
                  }}
                >
                  {(f) => (
                    <f.Checkbox>
                      <span
                        // Full-strength text instead of the Checkbox's muted
                        // gray (which reads as disabled), with blue policy
                        // links that underline on hover only.
                        className="text-foreground [&_a]:text-primary [&_a:hover]:underline"
                        dangerouslySetInnerHTML={{
                          __html: sanitizeLinksOnly(label),
                        }}
                      />
                    </f.Checkbox>
                  )}
                </form.AppField>
              )}
            </div>
          );
        })}
      </div>

      {!!resolved.formHint?.length && (
        <div className="mt-4 w-full text-sm" data-testid="form-hint">
          <RichText nodes={resolved.formHint} />
        </div>
      )}

      <form.Subscribe selector={(s) => s.isSubmitting}>
        {(isSubmitting) => (
          <div className="mt-4 flex justify-center">
            <Button
              type="submit"
              variant={resolved.submitButtonStyle ?? 'primary'}
              disabled={isSubmitting}
            >
              {isSubmitting
                ? '…'
                : resolved.submitButtonLabel || tForm('submitButton')}
            </Button>
          </div>
        )}
      </form.Subscribe>
    </form>
  );
}
